/* eslint-disable @typescript-eslint/no-explicit-any */
import { Col, Row } from '@design-system/components/grid/grid';
import { DeactivateModal } from './DeactivatingModal';
import * as styles from './DiscountDetails.view.css';
import {
  DiscountDetailsVM,
  filterTypeOptions,
} from '../view-model/discountDetails.viewModel';
import { GridList, toGridListSchema } from './components/GridList';
import { Pill } from './components/Pill';
import { ListCellRenderer } from './components/ListCell';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { IconButton } from '@design-system/components/button/iconButton';
import {
  faArrowUpRightFromSquare,
  faArrowDownToLine,
} from '@fortawesome/pro-regular-svg-icons';
import { TitledPage } from '@client/new/components/Misc';
import { Breadcrumbs } from '../../challenges/view/components/common/Breadcrumbs';
import { BackHeader } from '../../challenges/view/components/common/BackHeader';
import { MenuSelect } from './components/MenuSelect';
import { SearchInput } from '@design-system/components/searchinput';
import { clsx } from 'clsx';

export const DiscountsDetailsView = (p: DiscountDetailsVM) => (
  <Col>
    <Breadcrumbs
      breadcrumbs={[
        { name: 'Promotions', url: p.backURL },
        { name: 'Discount details' },
      ]}
    />
    <TitledPage title="Promotions">
      <div className="mt-5" style={{ maxWidth: '1200px' }}>
        <Col gap="xl">
          <Header {...p} />
          <ShareLinkCard {...p} />
          <PerformanceView {...p} />
          <UsersView {...p} />
          <DeactivateModal
            deactivationMode={p.deactivationMode}
            onConfirm={() => p.dispatch('FinishDeactivation')}
            onClose={() => p.dispatch('CloseDeactivating')}
          />
        </Col>
      </div>
    </TitledPage>
  </Col>
);

const Header = (p: DiscountDetailsVM) => (
  <BackHeader
    backURL={p.backURL}
    title={p.title}
    pillType={p.statusType}
    pillTitle={p.statusTitle}
    extraPill={
      p.freeTrialTitle
        ? { content: p.freeTrialTitle, type: 'Active' }
        : undefined
    }
  >
    {p.showDeactivateButton ? (
      <Button
        variant="text"
        size="medium"
        onClick={() => p.dispatch('StartDeactivateDiscount')}
      >
        <Typography color="ERROR-100">Deactivate</Typography>
      </Button>
    ) : null}
  </BackHeader>
);

const ShareLinkCard = (p: DiscountDetailsVM) => (
  <Row className={styles.card}>
    <Row alignCenter gap="l">
      <Col>
        <Typography variant="title" size="large">
          Get a sharable link
        </Typography>
      </Col>
      {p.isReferralCampaign ? (
        <Pill content="User referral" type="Primary" maxWidth="max-content" />
      ) : null}
      <Button
        variant="filled"
        size="medium"
        color="primaryTonal"
        style={{ color: '#350584' }}
        onClick={() => p.dispatch('CopyText', p.checkoutURL)}
        disabled={p.disableCheckoutLink}
      >
        Copy link
      </Button>
      <IconButton
        variant="filled"
        color="primaryTonal"
        size="medium"
        style={{ color: '#350584' }}
        icon={faArrowUpRightFromSquare}
        disabled={p.disableCheckoutLink}
      >
        <a
          href={p.checkoutURL}
          target="_blank"
          rel="noreferrer"
          style={{ color: '#350584' }}
          className={clsx(p.disableCheckoutLink && 'pointer-events-none')}
        >
          Open checkout page
        </a>
      </IconButton>
    </Row>
    <Typography size="medium" style={{ paddingRight: 300, fontWeight: 400 }}>
      Use a sharable link to promote your discount code via email or social
      media. This discount will be applied at checkout when customers use this
      link
    </Typography>
  </Row>
);

const PerformanceView = (p: DiscountDetailsVM) => (
  <Row className={styles.card}>
    <Typography variant="title" size="large">
      Performance
    </Typography>
    <Row>
      {p.performanceCards.map((e, i) => (
        <Col className={styles.stats} key={i}>
          {e.cells.map((ei, i2) => (
            <ListCellRenderer data={ei} key={i2} />
          ))}
        </Col>
      ))}
    </Row>
  </Row>
);

export const discountRowSchema = toGridListSchema([
  ['Purchase date', '1fr'],
  ['Name', '1fr'],
  ['Email', '1fr'],
  ['User Type', '1fr'],
]);

const UsersView = (p: DiscountDetailsVM) => (
  <Row className={styles.card}>
    <Row alignCenter gap="l">
      <Col>
        <Typography variant="title" size="large">
          Users
        </Typography>
      </Col>
      {p.userRows.length ? (
        <IconButton
          variant="text"
          size="medium"
          color="primary"
          icon={faArrowDownToLine}
          onClick={() => p.dispatch('ExportCSV')}
        >
          Export
        </IconButton>
      ) : null}
    </Row>
    <Row className={styles.filtersRow}>
      <SearchInput
        inputWrapperClassName={styles.searchWrapper}
        placeholder="Search by name or type"
        value={p.search}
        onChange={(e) => p.dispatch('SetSearch', e.target.value)}
        onClear={() => p.dispatch('SetSearch', '')}
      />
      <MenuSelect
        options={filterTypeOptions}
        onChange={(e) => p.dispatch('SetFilterType', e.target.value as any)}
        value={p.filters.type}
        name="filterType"
      />
    </Row>
    {p.userRows.length > 0 ? (
      <GridList schema={discountRowSchema} rows={p.userRows} variant="small" />
    ) : p.filters.type ? (
      <Typography style={{ fontWeight: 400 }}>
        No users match the current filter. Adjust the filter to see more.
      </Typography>
    ) : (
      <Typography style={{ fontWeight: 400 }}>
        No users yet. They’ll show up here once the discount is redeemed.
      </Typography>
    )}
  </Row>
);
